<template>
  <div class="config-container">
    <h2 class="page-title">sky mobi 配置</h2>
    <div class="table-section">
      <div class="toolbar">
        <span class="toolbar-title">应用列表</span>
        <el-button type="primary" @click="openDialog()">新建应用</el-button>
      </div>
      <el-table :data="appList" border stripe style="width: 100%;">
        <el-table-column prop="name" label="应用名称" min-width="160" />
        <el-table-column prop="package_name" label="package_name" min-width="220" />
        <el-table-column prop="configCount" label="配置数" width="100" />
        <el-table-column prop="updatedAt" label="更新时间" min-width="160" />
        <el-table-column label="操作" width="260" fixed="right">
          <template #default="scope">
            <el-button size="small" type="primary" @click="enterApp(scope.row)">管理配置</el-button>
            <el-button size="small" @click="openDialog(scope.row)">编辑</el-button>
            <el-button size="small" type="danger" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <el-dialog
      v-model="dialogVisible"
      :title="form.id ? '编辑应用' : '新建应用'"
      width="520px"
      :close-on-click-modal="false"
    >
      <el-form :model="form" label-width="120px">
        <el-form-item label="应用名称" required>
          <el-input v-model="form.name" placeholder="例如：Demo App" />
        </el-form-item>
        <el-form-item label="package_name" required>
          <el-input
            v-model="form.package_name"
            placeholder="例如：com.example.app"
            :disabled="!!form.id"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="saveApp">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { SkyMobiAppApi, type SkyMobiAppItem } from '../../api/skyMobiAppApi'

const router = useRouter()
const appList = ref<SkyMobiAppItem[]>([])
const dialogVisible = ref(false)
const saving = ref(false)
const form = ref({
  id: '',
  name: '',
  package_name: '',
})

async function loadList() {
  appList.value = await SkyMobiAppApi.getList()
}

function enterApp(row: SkyMobiAppItem) {
  router.push({
    name: 'SkyMobiAppConfigs',
    params: { packageName: row.package_name },
  })
}

function openDialog(row?: SkyMobiAppItem) {
  if (row) {
    form.value = {
      id: row.id,
      name: row.name || '',
      package_name: row.package_name || '',
    }
  } else {
    form.value = { id: '', name: '', package_name: '' }
  }
  dialogVisible.value = true
}

async function saveApp() {
  const name = form.value.name.trim()
  const packageName = form.value.package_name.trim()
  if (!name) {
    ElMessage.warning('请填写应用名称')
    return
  }
  if (!form.value.id && !packageName) {
    ElMessage.warning('请填写 package_name')
    return
  }
  saving.value = true
  try {
    if (form.value.id) {
      await SkyMobiAppApi.update(form.value.id, { name })
      ElMessage.success('已更新')
    } else {
      await SkyMobiAppApi.create({ name, package_name: packageName })
      ElMessage.success('已创建')
    }
    dialogVisible.value = false
    await loadList()
  } catch (error: any) {
    ElMessage.error(error?.response?.data?.message || error?.message || '保存失败')
  } finally {
    saving.value = false
  }
}

async function handleDelete(row: SkyMobiAppItem) {
  try {
    await ElMessageBox.confirm(
      `确定删除应用「${row.name}」？其下所有配置也会一并删除。`,
      '提示',
      { type: 'warning' },
    )
    await SkyMobiAppApi.delete(row.id)
    ElMessage.success('已删除')
    await loadList()
  } catch (error: any) {
    if (error !== 'cancel') {
      ElMessage.error(error?.response?.data?.message || error?.message || '删除失败')
    }
  }
}

onMounted(() => {
  loadList().catch((error: any) => {
    ElMessage.error(error?.response?.data?.message || error?.message || '加载失败')
  })
})
</script>

<style scoped>
.config-container {
  padding: 8px;
}
.page-title {
  margin: 0 0 16px;
  font-size: 20px;
}
.table-section {
  background: var(--el-bg-color);
  padding: 16px;
  border-radius: 8px;
}
.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.toolbar-title {
  font-weight: 600;
}
</style>
